import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Config } from 'config/envConfig';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircle,
    faFilter,
    faListCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
    Button,
    ConfirmModal,
    Dropdown,
    SearchFilterInput,
    LoadingView,
    alert,
    AlertType,
} from '@quyva/shared-ui';
import { SUSPEND_EVENT_TYPE_USER_INPUT } from '@quyva/common';
import axios from 'axios';
import { getFastApiUrl } from '../../utils/getUrl';
import Navbar from '../navbar';
import { CollapsedSideBar } from '../collapsedSideBar';
import { NotificationsColors } from '../../styles/notificationsColors';
import { mapAgentUpdateToRow } from '../../utils/agentsUpdatesUtils';
import {
    AgentsUpdatesTablePane,
    buildAgentsActionRenderer,
} from './AgentsUpdatesTablePane';
import { AgentsUpdatesDetailsPane } from './AgentsUpdatesDetailsPane';

const AGENTS_UPDATES_STATE_STORAGE_KEY_PREFIX = 'agentsUpdatesPageState_v1';

const getPrimaryAgentRowKey = (row) => String(row?.mergeKey || row?.id || '');

const getAgentRowAliasKeys = (row) =>
    Array.from(
        new Set([getPrimaryAgentRowKey(row), row?.syntheticMergeKey].filter(Boolean).map(String))
    );

const findExistingAgentRowMatch = (map, row) => {
    const primaryKey = getPrimaryAgentRowKey(row);
    const candidateKeys = getAgentRowAliasKeys(row);

    for (const key of candidateKeys) {
        if (map.has(key)) {
            return {
                existingKey: key,
                existingRow: map.get(key),
                primaryKey,
                candidateKeys,
            };
        }
    }

    return {
        existingKey: null,
        existingRow: null,
        primaryKey,
        candidateKeys,
    };
};

const upsertAgentRow = (map, row, mergedRow) => {
    const { existingKey, primaryKey, candidateKeys } = findExistingAgentRowMatch(map, row);
    map.set(primaryKey, mergedRow);

    candidateKeys.forEach((key) => {
        if (key !== primaryKey && map.has(key)) {
            map.delete(key);
        }
    });

    if (existingKey && existingKey !== primaryKey && map.has(existingKey)) {
        map.delete(existingKey);
    }
};

const AgentsUpdatesPage = ({
    newNotification,
    setNewNotification,
    viewNotifications,
    setViewNotifications,
    messages,
    socket,
}) => {
    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('current')) || null;
        } catch (e) {
            return null;
        }
    }, []);

    const stateStorageKey = useMemo(() => {
        const email = currentUser?.email ? String(currentUser.email) : 'anonymous';
        return `${AGENTS_UPDATES_STATE_STORAGE_KEY_PREFIX}:${email}`;
    }, [currentUser?.email]);

    let persistedState = null;
    try {
        const raw = localStorage.getItem(stateStorageKey);
        persistedState = raw ? JSON.parse(raw) : null;
    } catch (e) {
        persistedState = null;
    }

    const [search, setSearch] = useState(persistedState?.search || '');
    const [statusFilter, setStatusFilter] = useState(
        persistedState?.statusFilter || 'All'
    );
    const [userFilter, setUserFilter] = useState(
        persistedState?.userFilter || 'All'
    );
    const [selectedRowId, setSelectedRowId] = useState(
        persistedState?.selectedRowId || null
    );

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [taskToCancel, setTaskToCancel] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const [taskToResume, setTaskToResume] = useState(null);
    const [showResumeConfirm, setShowResumeConfirm] = useState(false);
    const [resumeDisabledByProcessId, setResumeDisabledByProcessId] = useState({});
    // Which suspended process ids the socket feed has reported, so the suspension
    // overlay is re-read exactly once per change instead of on every notification.
    const suspendedSignatureRef = useRef('');

    const [hadPersistedSelection] = useState(Boolean(persistedState?.selectedRowId));

    const leftTableContainerRef = useRef(null);
    const [leftTableContainerWidth, setLeftTableContainerWidth] = useState(0);
    const [leftTableContainerHeight, setLeftTableContainerHeight] = useState(0);
    const [hasUserDraggedSplitter, setHasUserDraggedSplitter] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(
                stateStorageKey,
                JSON.stringify({
                    search,
                    statusFilter,
                    userFilter,
                    selectedRowId,
                })
            );
        } catch (e) {
            // ignore
        }
    }, [search, statusFilter, userFilter, selectedRowId, stateStorageKey]);

    const fetchHistory = async () => {
        try {
            if (!currentUser?.email) return;

            setIsLoading(true);

            const formData = new FormData();
            formData.append('user_id', String(currentUser.email));

            const res = await axios.post(
                getFastApiUrl('/tool_manager/get_organization_tool_logs'),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            const processes = Array.isArray(res.data?.processes)
                ? res.data.processes
                : [];


            const mapped = processes
                .map((n) =>
                    mapAgentUpdateToRow({
                        notification: n,
                        currentUserEmail: currentUser?.email,
                    })
                )
                .filter(Boolean);

            setRows((prev) => {
                if (!prev || prev.length === 0) {
                    return mapped;
                }

                const map = new Map();
                (prev || []).forEach((row) => {
                    upsertAgentRow(map, row, row);
                });

                mapped.forEach((row) => {
                    const { existingRow } = findExistingAgentRowMatch(map, row);
                    upsertAgentRow(map, row, { ...(existingRow || {}), ...row });
                });

                return Array.from(map.values());
            });
        } catch (e) {
            console.warn('Failed to load organization tool logs', e?.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Merge in live updates
    useEffect(() => {
        if (!messages || !messages.length) return;

        const currentEmail = currentUser?.email;

        const agentMessages = messages.filter((m) => {
            const proc = String(m?.process || '').toLowerCase();
            return (
                proc === 'agent_execution' ||
                proc === 'async agent execution' ||
                proc === 'agent execution'
            );
        });


        const liveAgentRows = agentMessages
            .map((m) =>
                mapAgentUpdateToRow({
                    notification: m,
                    currentUserEmail: currentEmail,
                })
            )
            .filter(Boolean);


        if (!liveAgentRows.length) return;

        setRows((prev) => {
            const map = new Map();
            (prev || []).forEach((r) => upsertAgentRow(map, r, r));

            liveAgentRows.forEach((r) => {
                const { existingRow } = findExistingAgentRowMatch(map, r);
                const existing = existingRow || {};
                const shouldPreserveCreationTime =
                    existing.creationTime && (!r.creationTime || r.creationTime === '-');
                const shouldPreserveTimestamp =
                    existing.timestamp && (!r.timestamp || r.timestamp === '-');
                // Socket notifications carry nothing from suspended_scripts, so a live
                // update must not blank out an overlay already read from there - that
                // would lose the form of a task still waiting on it.
                const shouldPreserveSuspension =
                    !r.suspendEventType &&
                    !r.requiredInput &&
                    Boolean(existing.suspendEventType || existing.requiredInput);
                // Only the generic Paused is worth replacing with the suspension's more
                // specific Waiting. Any other live status (Processing, Success, Fail) means
                // the run has moved on and must win, or the row would be stranded on
                // Waiting until the page is reloaded.
                const shouldPreserveWaitingStatus =
                    shouldPreserveSuspension &&
                    String(r.status).toLowerCase() === 'paused' &&
                    String(existing.status).toLowerCase() === 'waiting';

                upsertAgentRow(map, r, {
                    ...existing,
                    ...r,
                    ...(shouldPreserveCreationTime
                        ? { creationTime: existing.creationTime }
                        : null),
                    ...(shouldPreserveTimestamp ? { timestamp: existing.timestamp } : null),
                    ...(shouldPreserveSuspension
                        ? {
                              suspendEventType: existing.suspendEventType,
                              requiredInput: existing.requiredInput,
                              suspensionId: existing.suspensionId,
                          }
                        : null),
                    ...(shouldPreserveWaitingStatus
                        ? { status: existing.status, progress: existing.progress }
                        : null),
                });
            });

            return Array.from(map.values());
        });

        // A run that parks while this page is open only announces itself over the socket,
        // which knows nothing of suspended_scripts. Re-read the feed so the overlay (and
        // with it the form) arrives, once per change in the set of suspended processes
        // rather than on every notification.
        const suspendedIds = liveAgentRows
            .filter(
                (r) =>
                    ['paused', 'waiting'].includes(String(r.status).toLowerCase()) &&
                    r.processId
            )
            .map((r) => String(r.processId))
            .sort()
            .join(',');

        if (suspendedIds !== suspendedSignatureRef.current) {
            suspendedSignatureRef.current = suspendedIds;
            if (suspendedIds) fetchHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, currentUser?.email]);

    useEffect(() => {
        const element = leftTableContainerRef.current;
        if (!element) return;

        const updateDims = () => {
            const rect = element.getBoundingClientRect();
            setLeftTableContainerWidth(rect.width);
            setLeftTableContainerHeight(rect.height);
        };

        updateDims();
        const observer = new ResizeObserver(() => updateDims());
        observer.observe(element);
        return () => observer.disconnect();
    }, [selectedRowId]);

    const selectedRow = useMemo(() => {
        if (!selectedRowId) return null;
        return (
            rows.find((r) => r.mergeKey === selectedRowId) ||
            rows.find((r) => r.id === selectedRowId) ||
            null
        );
    }, [selectedRowId, rows]);

    const statusOrder = [
        'Success',
        'Warning',
        'Fail',
        'Cancelled',
        'Waiting',
        'Paused',
        'Processing',
    ];

    const userIdsInData = useMemo(() => {
        return Array.from(new Set(rows.map((r) => r.userId).filter(Boolean))).sort();
    }, [rows]);

    const statusDropdownItems = [
        {
            key: 'All',
            label: 'All status',
            action: () => setStatusFilter('All'),
            selected: statusFilter === 'All',
        },
        ...statusOrder.map((s) => ({
            key: s,
            label: s,
            action: () => setStatusFilter(s),
            selected: statusFilter === s,
        })),
    ];

    const userDropdownItems = [
        {
            key: 'All',
            label: 'All users',
            action: () => setUserFilter('All'),
            selected: userFilter === 'All',
        },
        ...userIdsInData.map((id) => ({
            key: id,
            label: id,
            action: () => setUserFilter(id),
            selected: userFilter === id,
        })),
    ];

    const filteredRows = useMemo(() => {
        const searchLower = (search || '').toLowerCase();

        return (rows || [])
            .slice()
            .sort((a, b) => (b.lastUpdateMs || 0) - (a.lastUpdateMs || 0))
            .filter((r) => {
                const matchesSearch =
                    !searchLower ||
                    String(r.agentName || '').toLowerCase().includes(searchLower) ||
                    String(r.queryHeading || '').toLowerCase().includes(searchLower) ||
                    String(r.userId || '').toLowerCase().includes(searchLower);

                const matchesStatus =
                    statusFilter === 'All' || r.status === statusFilter;

                const matchesUser = userFilter === 'All' || r.userId === userFilter;

                return matchesSearch && matchesStatus && matchesUser;
            });
    }, [rows, search, statusFilter, userFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, statusFilter, userFilter]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Success':
                return NotificationsColors.SUCCESS;
            case 'Fail':
                return NotificationsColors.FAIL;
            case 'Warning':
                return NotificationsColors.WARNING;
            case 'Cancelled':
                return NotificationsColors.FAIL;
            case 'Waiting':
            case 'Paused':
                return NotificationsColors.WARNING;
            case 'Processing':
            default:
                return NotificationsColors.ONGOING;
        }
    };

    const renderStatusPill = (status) => {
        const s = String(status || 'Processing');

        const base =
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border';

        const classes = (() => {
            switch (s) {
                case 'Success':
                    return 'text-notifications-success border-notifications-success bg-notifications-success/10';
                case 'Fail':
                    return 'text-notifications-fail border-notifications-fail bg-notifications-fail/10';
                case 'Warning':
                    return 'text-notifications-warning border-notifications-warning bg-notifications-warning/10';
                case 'Cancelled':
                    return 'text-notifications-fail border-notifications-fail bg-notifications-fail/10';
                case 'Waiting':
                case 'Paused':
                    return 'text-landing-orange-600 border-landing-orange-500 bg-landing-orange-500/10';
                case 'Processing':
                default:
                    return 'text-notifications-onGoing border-notifications-onGoing bg-notifications-onGoing/10';
            }
        })();

        return (
            <span className={`${base} ${classes}`}>
                <FontAwesomeIcon icon={faCircle} className='text-[6px]' />
                {s}
            </span>
        );
    };

    const handleStop = (row) => {
        setTaskToCancel(row);
        setShowCancelConfirm(true);
    };

    const handleConfirmStop = () => {
        if (!socket || !taskToCancel) {
            setShowCancelConfirm(false);
            setTaskToCancel(null);
            return;
        }

        socket.emit('cancelTask', {
            userId: taskToCancel.rawUserMail,
            repoId: taskToCancel.repoId,
        });

        setShowCancelConfirm(false);
        setTaskToCancel(null);
    };

    // These are agent LOGS - an admin's view of everyone's runs. A suspension waiting on the
    // run's own user ('user_input', e.g. a form) is not the admin's to release: only that user
    // can supply the answers, and they do it from their own task tab. An admin resuming such a
    // run would continue it with no answers at all. Only a wait_for_event style pause, which
    // needs nothing but an approval, is resumable from here.
    const canResume = (row) =>
        row?.suspendEventType !== SUSPEND_EVENT_TYPE_USER_INPUT;

    const handleResume = (row) => {
        setTaskToResume(row);
        setShowResumeConfirm(true);
    };

    // resumeData stays null here: an approval carries no data. Form answers are collected in
    // the task tab, which passes them to the same endpoint.
    const sendResume = async (row, resumeData) => {
        const processId = row?.processId;
        if (!processId) {
            alert(AlertType.ERROR, 'No process id found for this agent execution.');
            return;
        }

        setResumeDisabledByProcessId((prev) => ({
            ...(prev || {}),
            [String(processId)]: true,
        }));

        try {
            const response = await axios.post(
                getFastApiUrl('/tool_utils/resolve_suspended_script'),
                {
                    // Explicitly a string: the endpoint keys on a String column, and sending a
                    // number is a 422.
                    process_id: String(processId),
                    should_resume: true,
                    resume_data: resumeData,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.data?.success) {
                setResumeDisabledByProcessId((prev) => {
                    const next = { ...(prev || {}) };
                    delete next[String(processId)];
                    return next;
                });
                alert(AlertType.ERROR, 'Failed to resume agent execution.');
                return;
            }

            alert(AlertType.SUCCESS, 'Your resume request has been sent.');
        } catch (e) {
            setResumeDisabledByProcessId((prev) => {
                const next = { ...(prev || {}) };
                delete next[String(processId)];
                return next;
            });
            alert(AlertType.ERROR, e?.message || 'Failed to resume agent execution');
        } finally {
            // Pick up whatever the run settled on - completed, failed, or parked again.
            fetchHistory();
        }
    };

    const handleConfirmResume = async () => {
        const row = taskToResume;
        if (!row) {
            setShowResumeConfirm(false);
            setTaskToResume(null);
            return;
        }

        // Check processId before the optimistic alert: a missing processId must
        // show only the error, not a false "resumed" success alert first.
        if (!row.processId) {
            alert(AlertType.ERROR, 'No process id found for this agent execution.');
            setShowResumeConfirm(false);
            setTaskToResume(null);
            return;
        }

        alert(
            AlertType.SUCCESS,
            'Your agent task has been resumed, you can check the status from task tab.'
        );

        setShowResumeConfirm(false);
        setTaskToResume(null);

        await sendResume(row, null);
    };

    const isResumeDisabled = (row) => {
        // While the confirm is open, block every row's Resume button so a second click
        // cannot queue up a different row behind it.
        if (showResumeConfirm) return true;
        const processId = row?.processId;
        if (!processId) return false;
        return Boolean(resumeDisabledByProcessId?.[String(processId)]);
    };

    const getResumeHoverText = (row) => {
        const q = String(row?.queryHeading || '').trim();
        if (!q) return 'Resume agent execution';
        return `Resume ${q} query`;
    };

    const columns = useMemo(() => {
        const shouldTruncate = true;
        const truncateText = (value, maxChars) => {
            const s = String(value || '');
            if (!s) return '-';
            if (!shouldTruncate) return s;
            if (s.length <= maxChars) return s;
            return `${s.slice(0, Math.max(0, maxChars - 3))}...`;
        };

        return [
            {
                title: '#',
                dataIndex: 'srNo',
                key: 'srNo',
                width: 60,
                render: (_v, _record, index) => (
                    <span className='whitespace-nowrap'>
                        {(currentPage - 1) * 9 + index + 1}
                    </span>
                ),
            },
            {
                title: 'AGENT NAME',
                dataIndex: 'agentName',
                key: 'agentName',
                width: 140,
                ellipsis: true,
                render: (v) => (
                    <span title={String(v || '')}>
                        {truncateText(v, 14)}
                    </span>
                ),
            },
            {
                title: 'QUERY',
                dataIndex: 'queryHeading',
                key: 'queryHeading',
                width: 180,
                ellipsis: true,
                render: (v) => (
                    <span title={String(v || '')}>
                        {truncateText(v, 18)}
                    </span>
                ),
            },
            {
                title: 'TIMESTAMP',
                dataIndex: 'creationTime',
                key: 'creationTime',
                sorter: (a, b) => (a.timestampMs || 0) - (b.timestampMs || 0),
                defaultSortOrder: 'descend',
                width: 160,
                title: <span className='whitespace-nowrap'>TIMESTAMP</span>,
                render: (v) => (
                    <span className='whitespace-nowrap'>{v || '-'}</span>
                ),
            },
            {
                title: 'STATUS',
                dataIndex: 'status',
                key: 'status',
                render: (s) => renderStatusPill(s),
                width: 110,
            },
            {
                title: 'USER ID',
                dataIndex: 'userId',
                key: 'userId',
                width: 90,
                ellipsis: true,
                render: (v) => (
                    <span title={String(v || '')}>
                        {truncateText(v, 10)}
                    </span>
                ),
            },
            {
                title: 'ACTION',
                dataIndex: 'actions',
                key: 'actions',
                width: 90,
                align: 'center',
                fixed: selectedRow ? 'right' : undefined,
                render: buildAgentsActionRenderer({
                    onStop: handleStop,
                    onResume: handleResume,
                    isResumeDisabled,
                    getResumeHoverText,
                    canResume,
                }),
            },
        ];
    }, [renderStatusPill, currentPage, selectedRow]);

    return (
        <div className='z-0 w-full h-[99vh] pt-2'>
            <ReflexContainer orientation='vertical'>
                <ReflexElement
                    className='left-pane mr-0.5'
                    minSize={50}
                    size={50}
                    maxSize={50}
                >
                    <div className='pane-content w-full h-full bg-transparent'>
                        <CollapsedSideBar
                            icon={faListCheck}
                            iconColor={'icons-primary'}
                            title={'Agents Updates'}
                            setIsRepoViewExpanded={() => {}}
                        />
                    </div>
                </ReflexElement>

                <ReflexSplitter
                    style={{
                        border: 0,
                        background: 'white',
                        width: '6px',
                        zIndex: 10,
                        height: '98vh',
                    }}
                />

                <ReflexElement className='right-pane ml-0.5'>
                    <div className='pane-content w-full h-full bg-transparent'>
                        <div className='w-full h-full border border-2 overflow-hidden bg-backgrounds-slate rounded-xl flex flex-col'>
                            <div className='w-full h-14 px-2 pt-2'>
                                <Navbar
                                    newNotification={newNotification}
                                    setNewNotification={setNewNotification}
                                    viewNotifications={viewNotifications}
                                    setViewNotifications={setViewNotifications}
                                    messages={messages}
                                    socket={socket}
                                />
                            </div>

                            <div className='flex-1 w-full h-full pt-6 pl-2 pr-0 pb-4'>
                                <div className='w-full h-full bg-backgrounds-white rounded-xl border border-backgrounds-border flex flex-col'>
                                    <div className='px-4 py-3 border-b border-backgrounds-border flex flex-wrap items-center gap-4 bg-backgrounds-primary100/60'>
                                        <div
                                            className='flex items-center text-texts-secondary300'
                                            title='Filter and search agent updates'
                                        >
                                            <FontAwesomeIcon
                                                icon={faFilter}
                                                className='text-sm text-texts-secondary300'
                                            />
                                        </div>

                                        <div className='flex-1 flex items-center gap-3 min-w-[260px]'>
                                            <SearchFilterInput
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                placeholder='Search by agent/query/user'
                                                className='flex-1 min-w-[380px]'
                                            />

                                            <div className='flex items-center gap-2 ml-auto text-xs'>
                                                <Dropdown
                                                    dropDownList={userDropdownItems}
                                                    showButtonText
                                                    buttonText={userFilter === 'All' ? 'All users' : String(userFilter || '')}
                                                    buttonClassName='h-8 w-[11rem] px-2 text-xs border border-backgrounds-border bg-backgrounds-white text-texts-secondary300'
                                                    listClassName='max-h-40 overflow-y-auto min-w-[11rem]'
                                                />
                                                <Dropdown
                                                    dropDownList={statusDropdownItems}
                                                    showButtonText
                                                    buttonText={statusFilter === 'All' ? 'All status' : String(statusFilter || '')}
                                                    buttonClassName='h-8 w-[8rem] px-2 text-xs border border-backgrounds-border bg-backgrounds-white text-texts-secondary300'
                                                    listClassName='max-h-40 overflow-y-auto min-w-[8rem]'
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className='flex-1 overflow-hidden'>
                                        {isLoading && (!rows || rows.length === 0) ? (
                                            <div className='w-full h-full flex items-center justify-center'>
                                                <LoadingView loadingText='Loading agent updates...' />
                                            </div>
                                        ) : !selectedRow ? (
                                            <AgentsUpdatesTablePane
                                                filteredRows={filteredRows}
                                                columns={columns}
                                                selectedRowId={selectedRowId}
                                                setSelectedRowId={setSelectedRowId}
                                                selectedRow={selectedRow}
                                                isCompact={false}
                                                leftTableContainerRef={leftTableContainerRef}
                                                leftTableContainerWidth={leftTableContainerWidth}
                                                leftTableContainerHeight={leftTableContainerHeight}
                                                hasUserDraggedSplitter={hasUserDraggedSplitter}
                                                setHasUserDraggedSplitter={setHasUserDraggedSplitter}
                                                currentPage={currentPage}
                                                setCurrentPage={setCurrentPage}
                                            />
                                        ) : (
                                            <ReflexContainer
                                                orientation='vertical'
                                                className='w-full h-full'
                                            >
                                                <ReflexElement
                                                    className='bg-backgrounds-white overflow-hidden rounded-l-xl rounded-r-none'
                                                    minSize={520}
                                                    flex={0.55}
                                                >
                                                    <AgentsUpdatesTablePane
                                                        filteredRows={filteredRows}
                                                        columns={columns}
                                                        selectedRowId={selectedRowId}
                                                        setSelectedRowId={setSelectedRowId}
                                                        selectedRow={selectedRow}
                                                        isCompact={true}
                                                        leftTableContainerRef={leftTableContainerRef}
                                                        leftTableContainerWidth={leftTableContainerWidth}
                                                        leftTableContainerHeight={leftTableContainerHeight}
                                                        hasUserDraggedSplitter={hasUserDraggedSplitter}
                                                        setHasUserDraggedSplitter={setHasUserDraggedSplitter}
                                                        currentPage={currentPage}
                                                        setCurrentPage={setCurrentPage}
                                                    />
                                                </ReflexElement>

                                                <ReflexSplitter
                                                    style={{
                                                        border: 0,
                                                        background: 'rgba(0, 0, 0, 0.06)',
                                                        width: '2px',
                                                        zIndex: 10,
                                                    }}
                                                    onStopResize={() => {
                                                        setHasUserDraggedSplitter(true);
                                                    }}
                                                />

                                                <ReflexElement
                                                    className='bg-backgrounds-white overflow-hidden rounded-r-xl rounded-l-none'
                                                    minSize={320}
                                                    flex={0.45}
                                                >
                                                    <AgentsUpdatesDetailsPane
                                                        selectedRow={selectedRow}
                                                        setSelectedRowId={setSelectedRowId}
                                                        renderStatusPill={renderStatusPill}
                                                    />
                                                </ReflexElement>
                                            </ReflexContainer>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </ReflexElement>
            </ReflexContainer>

            {showCancelConfirm && taskToCancel && (
                <ConfirmModal
                    modalText={`Are you sure you want to stop agent execution for ${taskToCancel.agentName}?`}
                    onConfirm={handleConfirmStop}
                    onCancel={() => {
                        setShowCancelConfirm(false);
                        setTaskToCancel(null);
                    }}
                />
            )}

            {showResumeConfirm && taskToResume && (
                <ConfirmModal
                    modalText={`Are you sure you want to resume the "agent execution" task of query ${taskToResume.queryHeading || taskToResume.agentName}?`}
                    onConfirm={handleConfirmResume}
                    onCancel={() => {
                        setShowResumeConfirm(false);
                        setTaskToResume(null);
                    }}
                />
            )}

        </div>
    );
};

export default AgentsUpdatesPage;
